import React, { useState } from 'react';
import EmailSignup from './components/EmailSignup';
import FeedbackForm from './components/FeedbackForm';
import spasskyScoresheet from './assets/spassky-scoresheet-messy.jpg';
import scoresheetGame from './assets/spassky-fischer-siegen-1970.gif';
import knightLogo from './assets/knight_no_background.png';
import appIcon from './assets/knight_w_background_rounded_1024x1024.png';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PsychologyIcon from '@mui/icons-material/Psychology';
import GamepadIcon from '@mui/icons-material/Gamepad';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import HandshakeIcon from '@mui/icons-material/Handshake';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import AndroidIcon from '@mui/icons-material/Android';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AssignmentIcon from '@mui/icons-material/Assignment';

function App() {
  const [activeTab, setActiveTab] = useState('signup');

  // Helper function to scroll to section with header offset
  const scrollToSection = (sectionId, customEvent = null) => {
    const element = document.getElementById(sectionId);
    const headerHeight = 80;
    const elementPosition = element.offsetTop - headerHeight;
    window.scrollTo({ top: elementPosition, behavior: 'smooth' });

    if (customEvent) {
      setTimeout(() => window.dispatchEvent(new CustomEvent(customEvent)), 100);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="text-2xl font-bold text-blue-600 hover:text-blue-700 transition-colors"
            >
              Tourni<em>mate</em>
            </button>
            <nav className="hidden md:flex space-x-8">
              <button
                onClick={() => scrollToSection('how-it-works')}
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                How It Works
              </button>
              <button
                onClick={() => scrollToSection('beta-access', 'focusBetaAccess')}
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                Join Waitlist
              </button>
              <button
                onClick={() => scrollToSection('feedback', 'focusFeedback')}
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                Feedback
              </button>
            </nav>
            <button
              onClick={() => {
                scrollToSection('beta-access', 'focusBetaAccess');
                setActiveTab('signup');
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700 transition-colors"
            >
              Join Waitlist
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Logo and Text Content */}
            <div className="text-left">
              {/* Logo and Title Row */}
              <div className="flex items-start gap-6 mb-6">
                {/* Logo */}
                <div className="flex-shrink-0">
                  <img
                    src={knightLogo}
                    alt="Tournimate Knight Logo"
                    className="h-36 md:h-44 w-auto object-contain"
                  />
                </div>

                {/* Title */}
                <div>
                  <div className="text-5xl md:text-6xl font-bold text-blue-900 mb-4">
                    Tourni<em>mate</em>
                  </div>
                  <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
                    <span className="text-blue-600">The Scoresheet Scanner That Thinks Like a Player</span>
                  </h1>
                </div>
              </div>

              {/* Description and Buttons - Aligned under logo */}
              <div className="ml-0">
                <p className="text-xl text-gray-600 mb-8">
                  <strong>Turn scoresheet chaos into clean games.</strong> Blundered your notation? We'll find the right move. Our chess intelligence goes beyond handwriting recognition to understand what you really played.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => scrollToSection('download-app')}
                    className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Get for iOS
                  </button>
                  <button
                    onClick={() => scrollToSection('download-app')}
                    className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold border-2 border-blue-600 hover:bg-blue-50 transition-colors"
                  >
                    Get for Android
                  </button>
                </div>
              </div>
            </div>

            {/* Right Column - Visual Flow */}
            <div className="flex items-center justify-center lg:justify-end">
              <div className="flex items-center gap-2 sm:gap-4 md:gap-6 max-w-full">
                {/* Scoresheet Image */}
                <div className="flex-shrink-0">
                  <img
                    src={spasskyScoresheet}
                    alt="Handwritten chess scoresheet with messy notation ready for OCR scanning and digitization"
                    className="h-40 sm:h-48 md:h-64 lg:h-80 w-auto rounded-lg shadow-lg"
                  />
                </div>

                {/* Arrow */}
                <div className="flex-shrink-0 text-blue-600 text-lg sm:text-xl md:text-2xl lg:text-3xl">
                  →
                </div>

                {/* Chess Game GIF */}
                <div className="flex-shrink-0">
                  <img
                    src={scoresheetGame}
                    alt="Digital chess game visualization showing converted PGN moves from scanned scoresheet"
                    className="h-40 sm:h-48 md:h-64 lg:h-80 w-auto rounded-lg shadow-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            How It Works
          </h2>
          <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto mb-12">
            Our inference engine knows chess and knows people. It understands what you meant to write, not just what you scribbled down.
          </p>

          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CameraAltIcon sx={{ fontSize: 40, color: '#2563eb' }} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">1. Take a Photo</h3>
                <p className="text-gray-600 text-lg">
                  Snap a picture of your handwritten chess scoresheet using your phone's camera
                </p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <PsychologyIcon sx={{ fontSize: 40, color: '#2563eb' }} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">2. AI Scans Your Moves</h3>
                <p className="text-gray-600 text-lg">
                  This is the easy part. A handwriting recognition model converts your image into text. We use state-of-the-art Optical Character Recognition to transcribe exactly what you wrote.
                </p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SearchIcon sx={{ fontSize: 40, color: '#2563eb' }} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">3. Figure Out What Really Happened</h3>
                <p className="text-gray-600 text-lg">
                  This is the hard part. Most of the time, there are errors in either the notation, the OCR output, or both. JADUUB, our chess autocorrect engine, asks 'based on what was written, what moves were actually played?'
                </p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <GamepadIcon sx={{ fontSize: 40, color: '#2563eb' }} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">4. Export to PGN</h3>
                <p className="text-gray-600 text-lg">
                  Export your game in PGN format or directly to a Lichess analysis board.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Unique Features Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">
              Beyond Handwriting: Chess Autocorrect
            </h2>
            <p className="text-xl mb-12">
              Other apps just read text. JADUUB is chess autocorrect that figures out what you meant, not just what you wrote. It corrects notation errors, finds missing moves, and fixes illegal moves automatically.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-blue-700 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <SearchIcon sx={{ fontSize: 28, color: 'white', marginRight: 1 }} />
                  <h3 className="text-2xl font-bold">"Where's That Move?"</h3>
                </div>
                <p className="text-lg">
                  Forgot to write move 23? JADUUB analyzes the position and tells you exactly what you played and where it belongs.
                </p>
              </div>
              <div className="bg-blue-700 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <EditIcon sx={{ fontSize: 28, color: 'white', marginRight: 1 }} />
                  <h3 className="text-2xl font-bold">"That's Not Legal!"</h3>
                </div>
                <p className="text-lg">
                  Wrote "Be3" but meant "Be6"? Can't read your own writing? JADUUB knows chess rules and fixes notation blunders and impossible moves automatically.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Waitlist & Release Section */}
      <section id="beta-access" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Coming Soon
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Join the waitlist to be notified when Tournimate launches and get early access
              </p>
            </div>

            {/* Sign Up Form */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <EmailSignup />
            </div>


            {/* What's Ready */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center justify-center mb-6">
                <AssignmentIcon sx={{ fontSize: 28, color: '#374151', marginRight: 1 }} />
                <h3 className="text-2xl font-bold text-gray-900">
                  What's Ready for Launch
                </h3>
              </div>

              {/* Launch Features */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <CheckCircleIcon sx={{ fontSize: 24, color: '#16a34a', marginRight: 1 }} />
                  <h4 className="text-xl font-semibold text-green-800">Launch Features</h4>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h5 className="font-semibold text-gray-900">Smart Scanning with Interactive Error Correction</h5>
                      <p className="text-gray-600 text-sm">AI-powered notation analysis with one-tap fixes</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h5 className="font-semibold text-gray-900">Export to PGN, Open Directly in Lichess</h5>
                      <p className="text-gray-600 text-sm">Seamless export and analysis workflow</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h5 className="font-semibold text-gray-900">English Algebraic Notation</h5>
                      <p className="text-gray-600 text-sm">Standard tournament notation support</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h5 className="font-semibold text-gray-900">Tournament Organization & Cloud Sync</h5>
                      <p className="text-gray-600 text-sm">Local storage with tournament grouping, synced across all devices</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h5 className="font-semibold text-gray-900">2-3 Minute Processing</h5>
                      <p className="text-gray-600 text-sm">Fast AI inference for most scoresheet types</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Future Features */}
              <div>
                <div className="flex items-center mb-4">
                  <AutoAwesomeIcon sx={{ fontSize: 24, color: '#1e40af', marginRight: 1 }} />
                  <h4 className="text-xl font-semibold text-blue-800">Coming Later</h4>
                </div>
                <p className="text-gray-600 text-sm mb-4 italic">Features planned for future updates</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-3 h-3 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h5 className="font-semibold text-gray-900">Auto Grid Detection</h5>
                      <p className="text-gray-600 text-sm">Automatically detect scoresheet layout</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-3 h-3 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h5 className="font-semibold text-gray-900">Faster Processing Time</h5>
                      <p className="text-gray-600 text-sm">Reduced processing time for scoresheet analysis</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-3 h-3 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h5 className="font-semibold text-gray-900">In-App Analysis</h5>
                      <p className="text-gray-600 text-sm">Built-in engine analysis</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-3 h-3 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h5 className="font-semibold text-gray-900">Language Support</h5>
                      <p className="text-gray-600 text-sm">Multiple notation languages</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-3 h-3 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h5 className="font-semibold text-gray-900">User Suggestions</h5>
                      <p className="text-gray-600 text-sm">Features based on your feedback</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download App Section */}
      <section id="download-app" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-4 mb-6">
              <img
                src={appIcon}
                alt="Tournimate App Icon"
                className="w-16 h-16 rounded-xl shadow-md"
              />
              <h2 className="text-4xl font-bold text-gray-900">
                Download Tournimate
              </h2>
            </div>
            <p className="text-xl text-gray-600 mb-12">
              Get started with intelligent scoresheet scanning on your device
            </p>

            <div className="grid md:grid-cols-2 gap-12">
              {/* iOS Download */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">iOS</h3>

                {/* QR Code Placeholder */}
                <div className="w-48 h-48 mx-auto mb-6 bg-gray-200 rounded-lg flex items-center justify-center">
                  <div className="text-gray-500 text-center">
                    <PhoneAndroidIcon sx={{ fontSize: 48, color: '#6b7280', marginBottom: 1 }} />
                    <p className="text-lg font-semibold text-blue-600 mb-1">Coming Soon!</p>
                    <p className="text-sm">QR Code for<br />App Store</p>
                  </div>
                </div>

                {/* App Store Button */}
                <button
                  onClick={() => scrollToSection('beta-access', 'focusBetaAccess')}
                  className="inline-block bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                >
                  Download on the App Store
                </button>
              </div>

              {/* Android Download */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Android</h3>

                {/* QR Code Placeholder */}
                <div className="w-48 h-48 mx-auto mb-6 bg-gray-200 rounded-lg flex items-center justify-center">
                  <div className="text-gray-500 text-center">
                    <AndroidIcon sx={{ fontSize: 48, color: '#6b7280', marginBottom: 1 }} />
                    <p className="text-lg font-semibold text-blue-600 mb-1">Coming Soon!</p>
                    <p className="text-sm">QR Code for<br />Google Play</p>
                  </div>
                </div>

                {/* Google Play Button */}
                <button
                  onClick={() => scrollToSection('beta-access', 'focusBetaAccess')}
                  className="inline-block bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                >
                  Get it on Google Play
                </button>
              </div>
            </div>

            <div className="mt-12 p-6 bg-blue-50 rounded-lg">
              <p className="text-blue-700">
                <strong>Coming Soon:</strong> Download links will be available on launch day. Join the waitlist to be notified when Tournimate is ready.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Feedback Section */}
      <section id="feedback" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-gray-50 rounded-xl shadow-lg p-8">
              <FeedbackForm />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="text-2xl font-bold text-blue-400 mb-4">
                Tourni<em>mate</em>
              </div>
              <p className="text-gray-300 mb-4 max-w-md">
                The intelligent chess scoresheet scanner that thinks like a player. Stop wasting hours fixing notation errors.
              </p>
              <p className="text-gray-400 text-sm">
                © 2025 Tournimate. All rights reserved.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => scrollToSection('how-it-works')}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    How It Works
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      scrollToSection('beta-access', 'focusBetaAccess');
                    }}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Join Waitlist
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('download-app')}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Download App
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      scrollToSection('feedback', 'focusFeedback');
                    }}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Feedback
                  </button>
                </li>
                <li>
                  <a
                    href="/privacy"
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a
                    href="/terms"
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact & Support */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => {
                      scrollToSection('feedback', 'focusFeedback');
                    }}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Contact Us
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      scrollToSection('beta-access', 'focusBetaAccess');
                    }}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Waitlist
                  </button>
                </li>
                <li>
                  <span className="text-gray-300">
                    Launch: Coming Soon
                  </span>
                </li>
              </ul>
            </div>
          </div>

        </div>
      </footer>

    </div>
  );
}

export default App;
